import { useState } from 'react';
import { Plus, Minus, ArrowRight, ArrowLeft, Building, Upload, X, Image as ImageIcon } from 'lucide-react';
import { uploadMultipleImages } from '../../utils/imageUpload';

interface ApplicationFormProps {
  shop: any;
  currentStep: number;
  formData: any;
  setFormData: (data: any) => void;
  setCurrentStep: (step: number) => void;
  onSubmit: (data: any) => void;
}

export default function ApplicationForm({ 
  shop, 
  currentStep, 
  formData, 
  setFormData, 
  setCurrentStep, 
  onSubmit 
}: ApplicationFormProps) {
  const [errors, setErrors] = useState<any>({});
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const updateFormData = (section: string, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
    if (errors[`${section}.${field}`]) {
      setErrors(prev => ({
        ...prev,
        [`${section}.${field}`]: null
      }));
    }
  };

  const addGuarantor = () => {
    setFormData(prev => ({
      ...prev,
      LeaseGuarantee: {
        ...prev.LeaseGuarantee,
        Guarantors: [...prev.LeaseGuarantee.Guarantors, '']
      }
    }));
  };

  const removeGuarantor = (index: number) => {
    setFormData(prev => ({
      ...prev,
      LeaseGuarantee: {
        ...prev.LeaseGuarantee,
        Guarantors: prev.LeaseGuarantee.Guarantors.filter((_, i) => i !== index)
      }
    }));
  };

  const updateGuarantor = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      LeaseGuarantee: {
        ...prev.LeaseGuarantee,
        Guarantors: prev.LeaseGuarantee.Guarantors.map((g, i) => i === index ? value : g)
      }
    }));
  };

  const updateSignature = (index: number, field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      Consent: {
        ...prev.Consent,
        TenantSignatures: prev.Consent.TenantSignatures.map((sig, i) => 
          i === index ? { ...sig, [field]: value } : sig
        )
      }
    }));
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadFiles = async () => {
    if (selectedFiles.length === 0) return;

    setIsUploading(true);
    try {
      const result = await uploadMultipleImages(selectedFiles);
      
      if (result.success) {
        setUploadedImages(prev => [...prev, ...result.urls]);
        setFormData(prev => ({
          ...prev,
          Images: [...(prev.Images || []), ...result.urls]
        }));
        setSelectedFiles([]);
      } else {
        alert(`Upload failed: ${result.errors.join(', ')}`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload images. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const removeUploadedImage = (index: number) => {
    const newImages = uploadedImages.filter((_, i) => i !== index);
    setUploadedImages(newImages);
    setFormData(prev => ({
      ...prev,
      Images: newImages
    }));
  };

  const validateStep = (step: number) => {
    const newErrors: any = {};

    switch (step) {
      case 1:
        if (!formData.Company.CompanyName.trim()) {
          newErrors['Company.CompanyName'] = 'Company name is required';
        }
        if (!formData.Company.EmailAddress.trim()) {
          newErrors['Company.EmailAddress'] = 'Email address is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.Company.EmailAddress)) {
          newErrors['Company.EmailAddress'] = 'Please enter a valid email address';
        }
        if (!formData.Company.PhoneNumber.trim()) {
          newErrors['Company.PhoneNumber'] = 'Phone number is required';
        } else if (!/^[\+]?[0-9\s\-\(\)]{10,15}$/.test(formData.Company.PhoneNumber.replace(/\s/g, ''))) {
          newErrors['Company.PhoneNumber'] = 'Please enter a valid phone number';
        }
        if (!formData.Images || formData.Images.length === 0) {
          newErrors['Images'] = 'At least one image is required';
        }
        break;
      case 2:
        // Guarantors are optional
        break;
      case 3:
        // Rental history is optional
        break;
      case 4:
        // No required fields for financials step
        break;
      case 5:
        if (!formData.Consent.ApplicantName.trim()) {
          newErrors['Consent.ApplicantName'] = 'Applicant name is required';
        }
        formData.Consent.TenantSignatures.forEach((sig, index) => {
          if (!sig.Signature.trim()) {
            newErrors[`Consent.TenantSignatures.${index}.Signature`] = 'Signature is required';
          }
        });
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = () => {
    if (validateStep(currentStep)) {
      onSubmit(formData);
    }
  };

  const getError = (path: string) => {
    return errors[path] || null;
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Step 0: Shop Information */}
      {currentStep === 0 && (
        <div className="space-y-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Shop Selection</h3>
          
          <div className="bg-light-gray rounded-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <Building className="h-5 w-5 text-primary" />
              <h4 className="font-semibold text-gray-900">Selected Shop</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">Shop Number:</span>
                <p className="text-gray-900">{shop?.shopNumber || 'N/A'}</p>
              </div>
              <div>
                <span className="font-medium text-gray-700">Floor:</span>
                <p className="text-gray-900">{shop?.floor || 'N/A'}</p>
              </div>
              <div>
                <span className="font-medium text-gray-700">Size:</span>
                <p className="text-gray-900">{shop?.size ? `${shop.size} sqm` : 'N/A'}</p>
              </div>
              <div>
                <span className="font-medium text-gray-700">Purpose:</span>
                <p className="text-gray-900">{shop?.purpose || 'N/A'}</p>
              </div>
            </div>
          </div>

          <div className="text-sm text-gray-600">
            <p>You are applying to lease this shop at Gandi Mall. Please review the shop details above and proceed with your application.</p>
          </div>
        </div>
      )}

      {/* Step 1: Company Information */}
      {currentStep === 1 && (
        <div className="space-y-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Your Company Information</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Name *
              </label>
              <input
                type="text"
                value={formData.Company.CompanyName}
                onChange={(e) => updateFormData('Company', 'CompanyName', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                  getError('Company.CompanyName') ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter company name"
              />
              {getError('Company.CompanyName') && (
                <p className="text-red-500 text-sm mt-1">{getError('Company.CompanyName')}</p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Principal Office Address
              </label>
              <textarea
                value={formData.Company.PrincipalOfficeAddress}
                onChange={(e) => updateFormData('Company', 'PrincipalOfficeAddress', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                rows={3}
                placeholder="Enter complete address"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                value={formData.Company.PhoneNumber}
                onChange={(e) => updateFormData('Company', 'PhoneNumber', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                  getError('Company.PhoneNumber') ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="+263 XX XXX XXXX"
              />
              {getError('Company.PhoneNumber') && (
                <p className="text-red-500 text-sm mt-1">{getError('Company.PhoneNumber')}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                value={formData.Company.EmailAddress}
                onChange={(e) => updateFormData('Company', 'EmailAddress', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                  getError('Company.EmailAddress') ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="company@email.com"
              />
              {getError('Company.EmailAddress') && (
                <p className="text-red-500 text-sm mt-1">{getError('Company.EmailAddress')}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Entity Type
              </label>
              <select
                value={formData.Company.EntityType}
                onChange={(e) => updateFormData('Company', 'EntityType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">Select entity type</option>
                <option value="individual">Individual</option>
                <option value="partnership">Partnership</option>
                <option value="corporation">Corporation</option>
                <option value="llc">LLC</option>
                <option value="trust">Trust</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                State of Incorporation
              </label>
              <input
                type="text"
                value={formData.Company.StateOfIncorporation}
                onChange={(e) => updateFormData('Company', 'StateOfIncorporation', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Zimbabwe"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Business Type
              </label>
              <select
                value={formData.Company.BusinessType}
                onChange={(e) => updateFormData('Company', 'BusinessType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">Select business type</option>
                <option value="Pharmacy">Pharmacy</option>
                <option value="Clothing Store">Clothing Store</option>
                <option value="Confectionary">Confectionary</option>
                <option value="Electronics">Electronics</option>
                <option value="Groceries">Groceries</option>
                <option value="Beauty Salon">Beauty Salon</option>
                <option value="Bookstore">Bookstore</option>
                <option value="Sports Goods">Sports Goods</option>
                <option value="Jewelry">Jewelry</option>
                <option value="Home & Garden">Home & Garden</option>
                <option value="Office">Office</option>
                <option value="Gaming Hub">Gaming Hub</option>
                <option value="Mens Wear">Mens Wear</option>
                <option value="Womens Wear">Womens Wear</option>
                <option value="Childrens Wear">Childrens Wear</option>
                <option value="Baby Wear">Baby Wear</option>
                <option value="Shoes">Shoes</option>
                <option value="Bags">Bags</option>
                <option value="Accessories">Accessories</option>
                <option value="Watches">Watches</option>
                <option value="PPE">PPE</option>
                <option value="Photo Studio">Photo Studio</option>
                <option value="Hand Bags">Hand Bags</option>
                <option value="Uniforms & Corporate Ware">Uniforms & Corporate Ware</option>
                <option value="Chinese">Chinese</option>
                <option value="Electric Appliances">Electric Appliances</option>
                <option value="Printing">Printing</option>
                <option value="Perfumes">Perfumes</option>
                <option value="Branding">Branding</option>
                <option value="Tailor">Tailor</option>
                <option value="Linen">Linen</option>
                <option value="Braids & Hair Products">Braids & Hair Products</option>
                <option value="Deco & Homeware">Deco & Homeware</option>
                <option value="Cosmetics">Cosmetics</option>
                <option value="Diapers">Diapers</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Image Upload Section */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Setup Images *
              </label>
              <p className="text-sm text-gray-600 mb-4">
                Upload photos of your current business setup. At least one image is required to complete your application.
                <br />
                <span className="block mt-1 text-gray-500">
                  For best results, include wide-angle shots and images that clearly show the overall layout and condition of your setup.
                </span>
              </p>
              {getError('Images') && (
                <p className="text-red-500 text-sm mb-4">{getError('Images')}</p>
              )}
              
              {/* File Input */}
              <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                getError('Images') ? 'border-red-500' : 'border-gray-300 hover:border-primary'
              }`}>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileChange}
                  className="hidden"
                  id="image-upload"
                />
                <label
                  htmlFor="image-upload"
                  className="cursor-pointer flex flex-col items-center gap-2"
                >
                  <Upload className="h-8 w-8 text-gray-400" />
                  <span className="text-sm font-medium text-gray-700">
                    Click to upload images
                  </span>
                  <span className="text-xs text-gray-500">
                    PNG, JPG, JPEG up to 10MB each
                  </span>
                </label>
              </div>

              {/* Selected Files */}
              {selectedFiles.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Selected Files:</h4>
                  <div className="space-y-2">
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                        <div className="flex items-center gap-2">
                          <ImageIcon className="h-4 w-4 text-gray-500" />
                          <span className="text-sm text-gray-700">{file.name}</span>
                          <span className="text-xs text-gray-500">
                            ({(file.size / 1024 / 1024).toFixed(2)} MB)
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={uploadFiles}
                    disabled={isUploading}
                    className="mt-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isUploading ? 'Uploading...' : 'Upload Images'}
                  </button>
                </div>
              )}

              {/* Uploaded Images */}
              {uploadedImages.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Uploaded Images:</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {uploadedImages.map((url, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={url}
                          alt={`Setup ${index + 1}`}
                          className="w-full h-24 object-cover rounded border"
                        />
                        <button
                          type="button"
                          onClick={() => removeUploadedImage(index)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Guarantors */}
      {currentStep === 2 && (
        <div className="space-y-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Lease Guarantors</h3>
          
          <div className="space-y-4">
            {formData.LeaseGuarantee.Guarantors.map((guarantor, index) => (
              <div key={index} className="flex gap-2 items-center">
                <input
                  type="text"
                  value={guarantor}
                  onChange={(e) => updateGuarantor(index, e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder={`Guarantor ${index + 1} name`}
                />
                {formData.LeaseGuarantee.Guarantors.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeGuarantor(index)}
                    className="text-red-500 hover:text-red-700 p-2"
                  >
                    <Minus className="h-5 w-5" />
                  </button>
                )}
              </div>
            ))}
            
            <button
              type="button"
              onClick={addGuarantor}
              className="flex items-center gap-2 text-primary hover:text-primary-dark text-sm"
            >
              <Plus className="h-4 w-4" />
              Add Guarantor
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Rental History */}
      {currentStep === 3 && (
        <div className="space-y-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Rental History</h3>
          
          <div className="space-y-6">
            <div className="bg-light-gray p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-4">Present Address</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                  <input
                    type="text"
                    value={formData.RentalHistory.PresentAddress}
                    onChange={(e) => updateFormData('RentalHistory', 'PresentAddress', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Current property address"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Rent/Own</label>
                  <select
                    value={formData.RentalHistory.PresentRentType}
                    onChange={(e) => updateFormData('RentalHistory', 'PresentRentType', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="rent">Rent</option>
                    <option value="own">Own</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Monthly Rent</label>
                  <input
                    type="number"
                    value={formData.RentalHistory.PresentRent}
                    onChange={(e) => updateFormData('RentalHistory', 'PresentRent', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Landlord Name</label>
                  <input
                    type="text"
                    value={formData.RentalHistory.PresentLandlordName}
                    onChange={(e) => updateFormData('RentalHistory', 'PresentLandlordName', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Landlord name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Landlord Phone</label>
                  <input
                    type="tel"
                    value={formData.RentalHistory.PresentLandlordPhone}
                    onChange={(e) => updateFormData('RentalHistory', 'PresentLandlordPhone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="+263 XX XXX XXXX"
                  />
                </div>
              </div>
            </div>

            <div className="bg-light-gray p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-4">Previous Address 1</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                  <input
                    type="text"
                    value={formData.RentalHistory.PreviousAddress1}
                    onChange={(e) => updateFormData('RentalHistory', 'PreviousAddress1', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Previous property address"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Rent/Own</label>
                  <select
                    value={formData.RentalHistory.PreviousRentType1}
                    onChange={(e) => updateFormData('RentalHistory', 'PreviousRentType1', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="rent">Rent</option>
                    <option value="own">Own</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Monthly Rent</label>
                  <input
                    type="number"
                    value={formData.RentalHistory.PreviousRent1}
                    onChange={(e) => updateFormData('RentalHistory', 'PreviousRent1', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Landlord Name</label>
                  <input
                    type="text"
                    value={formData.RentalHistory.PreviousLandlordName1}
                    onChange={(e) => updateFormData('RentalHistory', 'PreviousLandlordName1', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Landlord name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Landlord Phone</label>
                  <input
                    type="tel"
                    value={formData.RentalHistory.PreviousLandlordPhone1}
                    onChange={(e) => updateFormData('RentalHistory', 'PreviousLandlordPhone1', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="+263 XX XXX XXXX"
                  />
                </div>
              </div>
            </div>

            <div className="bg-light-gray p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-4">Previous Address 2</h4>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                <input
                  type="text"
                  value={formData.RentalHistory.PreviousAddress2}
                  onChange={(e) => updateFormData('RentalHistory', 'PreviousAddress2', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Second previous property address"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 4: Financials */}
      {currentStep === 4 && (
        <div className="space-y-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Financial Information</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Gross Revenue (Annual)
              </label>
              <input
                type="number"
                value={formData.Financials.GrossRevenue}
                onChange={(e) => updateFormData('Financials', 'GrossRevenue', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="0.00"
                min="0"
                step="0.01"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Total Expenses (Annual)
              </label>
              <input
                type="number"
                value={formData.Financials.TotalExpenses}
                onChange={(e) => updateFormData('Financials', 'TotalExpenses', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="0.00"
                min="0"
                step="0.01"
              />
            </div>

          </div>
        </div>
      )}

      {/* Step 5: Review & Sign */}
      {currentStep === 5 && (
        <div className="space-y-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Review & Sign</h3>
          
          <div className="bg-light-gray rounded-lg p-6">
            <h4 className="font-semibold text-gray-900 mb-4">Lease Application Summary</h4>
            <p className="text-sm text-gray-600 mb-4">You are applying to lease a shop at Gandi Mall. Please review your information below:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium">Company:</span>
                <p className="text-gray-600">{formData.Company.CompanyName}</p>
              </div>
              <div>
                <span className="font-medium">Email:</span>
                <p className="text-gray-600">{formData.Company.EmailAddress}</p>
              </div>
              <div>
                <span className="font-medium">Phone:</span>
                <p className="text-gray-600">{formData.Company.PhoneNumber}</p>
              </div>
              <div>
                <span className="font-medium">Business Type:</span>
                <p className="text-gray-600">{formData.Company.BusinessType || 'Not specified'}</p>
              </div>
              <div>
                <span className="font-medium">Guarantors:</span>
                <p className="text-gray-600">{formData.LeaseGuarantee.Guarantors.filter(g => g.trim()).join(', ') || 'None'}</p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Applicant Name *
              </label>
              <input
                type="text"
                value={formData.Consent.ApplicantName}
                onChange={(e) => updateFormData('Consent', 'ApplicantName', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                  getError('Consent.ApplicantName') ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter your full name"
              />
              {getError('Consent.ApplicantName') && (
                <p className="text-red-500 text-sm mt-1">{getError('Consent.ApplicantName')}</p>
              )}
            </div>


            <div className="space-y-4">
              <h4 className="font-semibold text-gray-900">Tenant Signatures</h4>
              {formData.Consent.TenantSignatures.map((signature, index) => (
                <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Signature {index + 1} *
                    </label>
                    <input
                      type="text"
                      value={signature.Signature}
                      onChange={(e) => updateSignature(index, 'Signature', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                        getError(`Consent.TenantSignatures.${index}.Signature`) ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Type your full name"
                    />
                    {getError(`Consent.TenantSignatures.${index}.Signature`) && (
                      <p className="text-red-500 text-sm mt-1">{getError(`Consent.TenantSignatures.${index}.Signature`)}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date {index + 1}
                    </label>
                    <input
                      type="date"
                      value={signature.Date}
                      onChange={(e) => updateSignature(index, 'Date', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
        <button
          onClick={prevStep}
          disabled={currentStep === 1}
          className={`flex items-center gap-2 px-6 py-2 rounded-lg transition-colors ${
            currentStep === 1
              ? 'text-gray-400 cursor-not-allowed'
              : 'text-gray-700 hover:text-primary'
          }`}
        >
          <ArrowLeft className="h-4 w-4" />
          Previous
        </button>

        {currentStep < 5 ? (
          <button
            onClick={nextStep}
            className="flex items-center gap-2 bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary-dark transition-colors btn-hover"
          >
            Next
            <ArrowRight className="h-4 w-4" />
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            className="px-6 py-2 rounded-lg transition-colors bg-primary text-white hover:bg-primary-dark btn-hover"
          >
            Submit Application
          </button>
        )}
      </div>
    </div>
  );
}